/**
 * D3.js visualization renderer for AskFelix
 *
 * This module handles rendering D3.js visualizations based on configurations
 * received from the backend.
 */

// D3 visualization renderer namespace
const d3Renderer = {
  // Global references to active visualizations for resize handling
  _activeVisualizations: {},

  /**
   * Renders a D3 visualization in the specified container
   *
   * @param {HTMLElement} container - The DOM element to render the visualization in
   * @param {Object} config - The D3 visualization configuration
   */
  render: function (container, config) {
    console.log('Rendering D3 visualization:', config);

    // Clear any existing content
    d3.select(container).html('');

    // Get container dimensions
    const containerRect = container.getBoundingClientRect();
    const containerWidth = Math.max(containerRect.width, 300);
    const containerHeight = Math.max(containerRect.height, 300);

    // Override dimensions in config for responsive behavior
    const d3Config = config.d3Config || {};
    d3Config.width = containerWidth;
    d3Config.height = containerHeight;
    config.d3Config = d3Config;

    // Determine which type of visualization to render
    const type = config.type || 'd3_custom';
    const visId = container.id || 'vis-' + Math.random().toString(36).substring(2, 9);

    // Store reference for resize handling
    this._activeVisualizations[visId] = {
      type,
      container,
      config,
      lastWidth: containerWidth,
      lastHeight: containerHeight
    };

    // Dispatch to the appropriate renderer
    if (type === 'force_directed') {
      this.renderForceDirectedGraph(container, config);
    } else if (type === 'tree_layout') {
      this.renderTreeLayout(container, config);
    } else if (type === 'network_graph') {
      this.renderNetworkGraph(container, config);
    } else {
      // Default generic D3 visualization
      this.renderCustomVisualization(container, config);
    }

    // Set up resize observation if not already set up
    if (!this._resizeObserver) {
      this._setupResizeHandling();
    }
  },

  /**
   * Set up resize handling for responsive visualizations
   */
  _setupResizeHandling: function () {
    // Use ResizeObserver if available
    if (window.ResizeObserver) {
      this._resizeObserver = new ResizeObserver(entries => {
        for (const entry of entries) {
          const container = entry.target;
          const visId = container.id;

          if (this._activeVisualizations[visId]) {
            const vis = this._activeVisualizations[visId];
            const newWidth = entry.contentRect.width;
            const newHeight = entry.contentRect.height;

            // Only redraw if size changed significantly (more than 10px)
            if (
              Math.abs(newWidth - vis.lastWidth) > 10 ||
              Math.abs(newHeight - vis.lastHeight) > 10
            ) {
              // Update dimensions
              vis.config.d3Config.width = newWidth;
              vis.config.d3Config.height = newHeight;
              vis.lastWidth = newWidth;
              vis.lastHeight = newHeight;

              // Redraw the visualization
              this.render(vis.container, vis.config);
            }
          }
        }
      });

      // Start observing all visualization containers
      for (const visId in this._activeVisualizations) {
        const container = this._activeVisualizations[visId].container;
        this._resizeObserver.observe(container);
      }
    } else {
      // Fallback to window resize event if ResizeObserver not available
      window.addEventListener(
        'resize',
        () => {
          for (const visId in this._activeVisualizations) {
            const vis = this._activeVisualizations[visId];
            const container = vis.container;
            const rect = container.getBoundingClientRect();

            if (
              Math.abs(rect.width - vis.lastWidth) > 10 ||
              Math.abs(rect.height - vis.lastHeight) > 10
            ) {
              // Update dimensions
              vis.config.d3Config.width = rect.width;
              vis.config.d3Config.height = rect.height;
              vis.lastWidth = rect.width;
              vis.lastHeight = rect.height;

              // Redraw the visualization
              this.render(vis.container, vis.config);
            }
          }
        },
        { passive: true }
      );
    }
  },

  /**
   * Renders a force-directed graph visualization
   *
   * @param {HTMLElement} container - The DOM element to render the visualization in
   * @param {Object} config - The visualization configuration
   */
  renderForceDirectedGraph: function (container, config) {
    const data = config.data;
    const d3Config = config.d3Config || {};
    const width = d3Config.width || 800;
    const height = d3Config.height || 600;

    // Create SVG container
    const svg = d3
      .select(container)
      .append('svg')
      .attr('width', width)
      .attr('height', height)
      .attr('viewBox', [0, 0, width, height]);

    // Create D3 force simulation
    const simulation = d3
      .forceSimulation(data.nodes)
      .force(
        'link',
        d3.forceLink(data.links).id(d => d.id)
      )
      .force('charge', d3.forceManyBody().strength(d3Config.strength || -30))
      .force('center', d3.forceCenter(width / 2, height / 2))
      .force('collision', d3.forceCollide().radius(d3Config.collisionRadius || 15));

    // Create the color scale
    const colorScale = d3.scaleOrdinal(d3[`scheme${d3Config.colorScheme || 'Category10'}`]);

    // Create links
    const link = svg
      .append('g')
      .attr('stroke', '#999')
      .attr('stroke-opacity', 0.6)
      .selectAll('line')
      .data(data.links)
      .join('line')
      .attr('stroke-width', d => Math.sqrt(d.value || 1));

    // Create nodes
    const node = svg
      .append('g')
      .attr('stroke', '#fff')
      .attr('stroke-width', 1.5)
      .selectAll('circle')
      .data(data.nodes)
      .join('circle')
      .attr('r', 8)
      .attr('fill', (d, i) => colorScale(i))
      .call(this.drag(simulation));

    // Add node titles (tooltip)
    node.append('title').text(d => d.id);

    // Add node labels if configured
    if (d3Config.showLabels) {
      const labels = svg
        .append('g')
        .selectAll('text')
        .data(data.nodes)
        .join('text')
        .attr('text-anchor', 'middle')
        .attr('dy', '.35em')
        .attr('font-size', d3Config.labelSize || 10)
        .text(d => d.id)
        .attr('pointer-events', 'none');

      // Update label positions during simulation
      simulation.on('tick', () => {
        link
          .attr('x1', d => d.source.x)
          .attr('y1', d => d.source.y)
          .attr('x2', d => d.target.x)
          .attr('y2', d => d.target.y);

        node.attr('cx', d => d.x).attr('cy', d => d.y);

        labels.attr('x', d => d.x).attr('y', d => d.y);
      });
    } else {
      // Update positions without labels
      simulation.on('tick', () => {
        link
          .attr('x1', d => d.source.x)
          .attr('y1', d => d.source.y)
          .attr('x2', d => d.target.x)
          .attr('y2', d => d.target.y);

        node.attr('cx', d => d.x).attr('cy', d => d.y);
      });
    }

    // Add title to the visualization
    if (config.title) {
      svg
        .append('text')
        .attr('x', width / 2)
        .attr('y', 20)
        .attr('text-anchor', 'middle')
        .style('font-size', '16px')
        .style('font-weight', 'bold')
        .text(config.title);
    }

    // Add zoom behavior if configured
    if (d3Config.interactive !== false) {
      svg.call(
        d3
          .zoom()
          .extent([
            [0, 0],
            [width, height]
          ])
          .scaleExtent([0.1, 8])
          .on('zoom', event => {
            node.attr('transform', event.transform);
            link.attr('transform', event.transform);
            if (d3Config.showLabels) {
              labels.attr('transform', event.transform);
            }
          })
      );
    }
  },

  /**
   * Renders a tree layout visualization
   *
   * @param {HTMLElement} container - The DOM element to render the visualization in
   * @param {Object} config - The visualization configuration
   */
  renderTreeLayout: function (container, config) {
    const data = config.data;
    const d3Config = config.d3Config || {};
    const width = d3Config.width || 900;
    const height = d3Config.height || 600;
    const margin = d3Config.margin || { top: 50, right: 120, bottom: 50, left: 120 };

    // Create SVG container
    const svg = d3
      .select(container)
      .append('svg')
      .attr('width', width)
      .attr('height', height)
      .attr('viewBox', [0, 0, width, height]);

    // Process the flat data into hierarchical structure
    const stratify = d3
      .stratify()
      .id(d => d.id)
      .parentId(d => d.parent);

    // Convert data to hierarchy
    let root;
    try {
      root = stratify(data);
      root.sort((a, b) => a.id.localeCompare(b.id));
    } catch (e) {
      console.error('Error creating tree hierarchy:', e);
      d3.select(container).html(
        `<div class="error">Error creating tree hierarchy: ${e.message}</div>`
      );
      return;
    }

    // Determine the layout based on orientation
    const nodeRadius = d3Config.nodeRadius || 5;
    const orientation = d3Config.orientation || 'vertical';
    let treeLayout;

    if (orientation === 'radial') {
      // Radial tree layout
      treeLayout = d3
        .tree()
        .size([2 * Math.PI, Math.min(width, height) / 2 - 120])
        .separation((a, b) => (a.parent === b.parent ? 1 : 2) / a.depth);
    } else {
      // Vertical or horizontal tree layout
      const innerWidth = width - margin.left - margin.right;
      const innerHeight = height - margin.top - margin.bottom;

      if (orientation === 'horizontal') {
        treeLayout = d3.tree().size([innerHeight, innerWidth]);
      } else {
        // vertical
        treeLayout = d3.tree().size([innerWidth, innerHeight]);
      }
    }

    // Apply the layout
    const nodes = treeLayout(root);

    // Add title to the visualization
    if (config.title) {
      svg
        .append('text')
        .attr('x', width / 2)
        .attr('y', 20)
        .attr('text-anchor', 'middle')
        .style('font-size', '16px')
        .style('font-weight', 'bold')
        .text(config.title);
    }

    // Create the tree group with proper margins
    const g = svg.append('g');

    if (orientation === 'radial') {
      // Center the visualization
      g.attr('transform', `translate(${width / 2},${height / 2})`);

      // Add links between nodes
      g.selectAll('.link')
        .data(nodes.links())
        .join('path')
        .attr('class', 'link')
        .attr('fill', 'none')
        .attr('stroke', '#999')
        .attr(
          'd',
          d3
            .linkRadial()
            .angle(d => d.x)
            .radius(d => d.y)
        );

      // Add nodes
      const node = g
        .selectAll('.node')
        .data(nodes.descendants())
        .join('g')
        .attr('class', 'node')
        .attr('transform', d => `translate(${radialPoint(d.x, d.y)})`);

      // Add circles for nodes
      node
        .append('circle')
        .attr('r', nodeRadius)
        .attr('fill', d => (d.children ? '#555' : '#999'));

      // Add node labels
      node
        .append('text')
        .attr('dy', '0.31em')
        .attr('x', d => (d.x < Math.PI ? 6 : -6))
        .attr('text-anchor', d => (d.x < Math.PI ? 'start' : 'end'))
        .attr('transform', d => (d.x >= Math.PI ? 'rotate(180)' : null))
        .text(d => d.data.id);

      // Helper function for radial coordinates
      function radialPoint(x, y) {
        return [(y = +y) * Math.cos((x -= Math.PI / 2)), y * Math.sin(x)];
      }
    } else {
      // Position the group with margins
      g.attr('transform', `translate(${margin.left},${margin.top})`);

      // Determine link generator based on orientation and style
      let linkGenerator;
      const linkStyle = d3Config.linkStyle || 'curve';

      if (orientation === 'horizontal') {
        if (linkStyle === 'straight') {
          linkGenerator = d3
            .linkHorizontal()
            .x(d => d.y)
            .y(d => d.x);
        } else if (linkStyle === 'elbow') {
          linkGenerator = d => {
            return `M${d.source.y},${d.source.x}
                    V${d.target.x}
                    H${d.target.y}`;
          };
        } else {
          // curve
          linkGenerator = d3
            .linkHorizontal()
            .x(d => d.y)
            .y(d => d.x);
        }
      } else {
        // vertical
        if (linkStyle === 'straight') {
          linkGenerator = d3
            .linkVertical()
            .x(d => d.x)
            .y(d => d.y);
        } else if (linkStyle === 'elbow') {
          linkGenerator = d => {
            return `M${d.source.x},${d.source.y}
                    H${d.target.x}
                    V${d.target.y}`;
          };
        } else {
          // curve
          linkGenerator = d3
            .linkVertical()
            .x(d => d.x)
            .y(d => d.y);
        }
      }

      // Add links
      g.selectAll('.link')
        .data(nodes.links())
        .join('path')
        .attr('class', 'link')
        .attr('fill', 'none')
        .attr('stroke', '#999')
        .attr('d', linkGenerator);

      // Add nodes
      const node = g
        .selectAll('.node')
        .data(nodes.descendants())
        .join('g')
        .attr('class', 'node')
        .attr('transform', d => {
          return orientation === 'horizontal'
            ? `translate(${d.y},${d.x})`
            : `translate(${d.x},${d.y})`;
        });

      // Add circles for nodes
      node
        .append('circle')
        .attr('r', nodeRadius)
        .attr('fill', d => (d.children ? '#555' : '#999'));

      // Add node labels
      node
        .append('text')
        .attr('dy', '0.31em')
        .attr('x', d => (orientation === 'horizontal' ? (d.children ? -6 : 6) : 0))
        .attr('y', d => (orientation === 'horizontal' ? 0 : d.children ? -6 : 6))
        .attr('text-anchor', d => {
          if (orientation === 'horizontal') {
            return d.children ? 'end' : 'start';
          } else {
            return 'middle';
          }
        })
        .text(d => d.data.id);
    }

    // Add zoom behavior if configured
    if (d3Config.interactive !== false) {
      svg.call(
        d3
          .zoom()
          .extent([
            [0, 0],
            [width, height]
          ])
          .scaleExtent([0.1, 8])
          .on('zoom', event => {
            g.attr('transform', event.transform);
          })
      );
    }
  },

  /**
   * Renders a network graph visualization
   *
   * @param {HTMLElement} container - The DOM element to render the visualization in
   * @param {Object} config - The visualization configuration
   */
  renderNetworkGraph: function (container, config) {
    // Similar to force directed but with additional features for network analysis
    const data = config.data;
    const d3Config = config.d3Config || {};
    const width = d3Config.width || 800;
    const height = d3Config.height || 600;

    // Create SVG container
    const svg = d3
      .select(container)
      .append('svg')
      .attr('width', width)
      .attr('height', height)
      .attr('viewBox', [0, 0, width, height]);

    // Create D3 force simulation with more customized forces
    const simulation = d3
      .forceSimulation(data.nodes)
      .force(
        'link',
        d3
          .forceLink(data.links)
          .id(d => d.id)
          .distance(d3Config.linkDistance || 100)
      )
      .force('charge', d3.forceManyBody().strength(d3Config.strength || -50))
      .force('center', d3.forceCenter(width / 2, height / 2))
      .force('collision', d3.forceCollide().radius(d3Config.nodeRadius || 10));

    // Add X and Y forces for grid layout if specified
    if (d3Config.layout === 'grid') {
      simulation.force('x', d3.forceX().strength(0.1)).force('y', d3.forceY().strength(0.1));
    }

    // Create arrow markers if directed graph
    if (d3Config.directed) {
      svg
        .append('defs')
        .selectAll('marker')
        .data(['end'])
        .join('marker')
        .attr('id', 'arrow')
        .attr('viewBox', '0 -5 10 10')
        .attr('refX', 15)
        .attr('refY', 0)
        .attr('markerWidth', 6)
        .attr('markerHeight', 6)
        .attr('orient', 'auto')
        .append('path')
        .attr('fill', '#999')
        .attr('d', 'M0,-5L10,0L0,5');
    }

    // Determine node size strategy
    const getNodeSize = d => {
      if (d3Config.nodeSize === 'fixed') {
        return d3Config.nodeSizeValue || 8;
      }

      // Count connections (degree)
      const connections = data.links.filter(
        l => l.source.id === d.id || l.target.id === d.id
      ).length;

      return 5 + Math.sqrt(connections * 3);
    };

    // Create links
    const link = svg
      .append('g')
      .attr('stroke', '#999')
      .attr('stroke-opacity', 0.6)
      .selectAll('line')
      .data(data.links)
      .join('line')
      .attr('stroke-width', d => Math.sqrt(d.value || 1))
      .attr('marker-end', d3Config.directed ? 'url(#arrow)' : null);

    // Create color scale for nodes
    const colorScale = d3.scaleOrdinal(d3[`scheme${d3Config.colorScheme || 'Category10'}`]);

    // Create nodes
    const node = svg
      .append('g')
      .attr('stroke', '#fff')
      .attr('stroke-width', 1.5)
      .selectAll('circle')
      .data(data.nodes)
      .join('circle')
      .attr('r', getNodeSize)
      .attr('fill', (d, i) => colorScale(i))
      .call(this.drag(simulation));

    // Add node titles (tooltip)
    node.append('title').text(d => {
      // Show all node attributes in tooltip
      let tooltip = `ID: ${d.id}`;
      for (const [key, value] of Object.entries(d)) {
        if (
          key !== 'id' &&
          key !== 'index' &&
          key !== 'x' &&
          key !== 'y' &&
          key !== 'vx' &&
          key !== 'vy'
        ) {
          tooltip += `\n${key}: ${value}`;
        }
      }
      return tooltip;
    });

    // Add node labels if configured
    if (d3Config.showLabels) {
      const labels = svg
        .append('g')
        .selectAll('text')
        .data(data.nodes)
        .join('text')
        .attr('text-anchor', 'middle')
        .attr('dy', '.35em')
        .attr('font-size', d3Config.labelSize || 10)
        .text(d => d.id)
        .attr('pointer-events', 'none');

      // Update positions including labels
      simulation.on('tick', () => {
        link
          .attr('x1', d => d.source.x)
          .attr('y1', d => d.source.y)
          .attr('x2', d => d.target.x)
          .attr('y2', d => d.target.y);

        node.attr('cx', d => d.x).attr('cy', d => d.y);

        labels.attr('x', d => d.x).attr('y', d => d.y);
      });
    } else {
      // Update positions without labels
      simulation.on('tick', () => {
        link
          .attr('x1', d => d.source.x)
          .attr('y1', d => d.source.y)
          .attr('x2', d => d.target.x)
          .attr('y2', d => d.target.y);

        node.attr('cx', d => d.x).attr('cy', d => d.y);
      });
    }

    // Add title to the visualization
    if (config.title) {
      svg
        .append('text')
        .attr('x', width / 2)
        .attr('y', 20)
        .attr('text-anchor', 'middle')
        .style('font-size', '16px')
        .style('font-weight', 'bold')
        .text(config.title);
    }

    // Add zoom behavior if configured
    if (d3Config.interactive !== false) {
      svg.call(
        d3
          .zoom()
          .extent([
            [0, 0],
            [width, height]
          ])
          .scaleExtent([0.1, 8])
          .on('zoom', event => {
            link.attr('transform', event.transform);
            node.attr('transform', event.transform);
            if (d3Config.showLabels) {
              labels.attr('transform', event.transform);
            }
          })
      );
    }
  },

  /**
   * Renders a custom D3 visualization
   *
   * @param {HTMLElement} container - The DOM element to render the visualization in
   * @param {Object} config - The visualization configuration
   */
  renderCustomVisualization: function (container, config) {
    const columns = config.columns || [];
    const d3Config = config.d3Config || {};
    const width = d3Config.width || 800;
    const height = d3Config.height || 600;
    const margin = d3Config.margin || { top: 50, right: 50, bottom: 50, left: 50 };

    function parseData(raw) {
      if (!raw) return [];
      if (Array.isArray(raw)) return raw;
      if (typeof raw === 'string') {
        try {
          return d3.csvParse(raw.trim());
        } catch (e) {
          console.error('Failed to parse csv string', e);
        }
      }
      return [];
    }

    function detectColumnTypes(data, cols) {
      const types = {};
      cols.forEach(col => {
        const sample = data.slice(0, 5).map(d => d[col]);
        if (sample.every(v => !isNaN(Date.parse(v)))) {
          types[col] = 'date';
        } else if (sample.every(v => !isNaN(parseFloat(v)))) {
          types[col] = 'numeric';
        } else {
          types[col] = 'category';
        }
      });
      return types;
    }

    function drawChart(data) {
      if (!data || !data.length) {
        d3.select(container).append('div').text('No data available');
        return;
      }

      const types = detectColumnTypes(data, columns.length ? columns : Object.keys(data[0]));
      const cols = columns.length ? columns : Object.keys(types);

      const svg = d3
        .select(container)
        .append('svg')
        .attr('width', width)
        .attr('height', height)
        .attr('viewBox', [0, 0, width, height]);

      if (config.title) {
        svg
          .append('text')
          .attr('x', width / 2)
          .attr('y', 20)
          .attr('text-anchor', 'middle')
          .style('font-size', '16px')
          .style('font-weight', 'bold')
          .text(config.title);
      }

      const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`);

      const innerWidth = width - margin.left - margin.right;
      const innerHeight = height - margin.top - margin.bottom;

      const [xCol, yCol] = cols;
      const xType = types[xCol];
      const yType = types[yCol];

      if (xType === 'date' && yType === 'numeric') {
        const xScale = d3
          .scaleTime()
          .domain(d3.extent(data, d => new Date(d[xCol])))
          .range([0, innerWidth]);
        const yScale = d3
          .scaleLinear()
          .domain(d3.extent(data, d => +d[yCol]))
          .range([innerHeight, 0])
          .nice();

        const line = d3
          .line()
          .x(d => xScale(new Date(d[xCol])))
          .y(d => yScale(+d[yCol]));

        g.append('path')
          .datum(data)
          .attr('fill', 'none')
          .attr('stroke', d3Config.colors ? d3Config.colors[0] : 'steelblue')
          .attr('stroke-width', 1.5)
          .attr('d', line);

        g.append('g').attr('transform', `translate(0,${innerHeight})`).call(d3.axisBottom(xScale));

        g.append('g').call(d3.axisLeft(yScale));
      } else if (xType === 'category' && yType === 'numeric') {
        const xScale = d3
          .scaleBand()
          .domain(data.map(d => d[xCol]))
          .range([0, innerWidth])
          .padding(0.1);

        const yScale = d3
          .scaleLinear()
          .domain([0, d3.max(data, d => +d[yCol])])
          .nice()
          .range([innerHeight, 0]);

        g.append('g').attr('transform', `translate(0,${innerHeight})`).call(d3.axisBottom(xScale));

        g.append('g').call(d3.axisLeft(yScale));

        g.selectAll('rect')
          .data(data)
          .join('rect')
          .attr('x', d => xScale(d[xCol]))
          .attr('y', d => yScale(+d[yCol]))
          .attr('width', xScale.bandwidth())
          .attr('height', d => innerHeight - yScale(+d[yCol]))
          .attr('fill', d3Config.colors ? d3Config.colors[0] : 'steelblue');
      } else if (cols.length >= 2) {
        const xScale = d3
          .scaleLinear()
          .domain(d3.extent(data, d => +d[xCol]))
          .range([0, innerWidth])
          .nice();

        const yScale = d3
          .scaleLinear()
          .domain(d3.extent(data, d => +d[yCol]))
          .range([innerHeight, 0])
          .nice();

        g.append('g').attr('transform', `translate(0,${innerHeight})`).call(d3.axisBottom(xScale));

        g.append('g').call(d3.axisLeft(yScale));

        g.selectAll('circle')
          .data(data)
          .join('circle')
          .attr('cx', d => xScale(+d[xCol]))
          .attr('cy', d => yScale(+d[yCol]))
          .attr('r', 4)
          .attr('fill', d3Config.colors ? d3Config.colors[0] : 'steelblue')
          .attr('opacity', 0.7);
      } else {
        g.append('text')
          .attr('x', innerWidth / 2)
          .attr('y', innerHeight / 2)
          .attr('text-anchor', 'middle')
          .text('Custom D3 visualization - insufficient data or columns');
      }

      if (d3Config.interactive !== false) {
        svg.call(
          d3
            .zoom()
            .extent([
              [0, 0],
              [width, height]
            ])
            .scaleExtent([0.5, 8])
            .on('zoom', event => {
              g.attr('transform', event.transform);
            })
        );
      }
    }

    if (config.csvUrl) {
      d3.csv(config.csvUrl)
        .then(drawChart)
        .catch(err => {
          console.error('Failed to load CSV', err);
          drawChart(parseData(config.data));
        });
    } else if (config.csvData) {
      drawChart(parseData(config.csvData));
    } else {
      drawChart(parseData(config.data));
    }

    // Create SVG container
    const svg = d3
      .select(container)
      .append('svg')
      .attr('width', width)
      .attr('height', height)
      .attr('viewBox', [0, 0, width, height]);

    // Add title to the visualization
    if (config.title) {
      svg
        .append('text')
        .attr('x', width / 2)
        .attr('y', 20)
        .attr('text-anchor', 'middle')
        .style('font-size', '16px')
        .style('font-weight', 'bold')
        .text(config.title);
    }

    // This is a placeholder for a generic visualization
    // In a real implementation, you would analyze the data and create a suitable visualization

    // Create a simple SVG visualization that shows data elements
    const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`);

    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;

    // If we have at least two columns, create a scatter plot
    if (columns.length >= 2 && data.length > 0) {
      const xColumn = columns[0];
      const yColumn = columns[1];

      // Create scales
      const xScale = d3
        .scaleLinear()
        .domain(d3.extent(data, d => +d[xColumn]))
        .range([0, innerWidth])
        .nice();

      const yScale = d3
        .scaleLinear()
        .domain(d3.extent(data, d => +d[yColumn]))
        .range([innerHeight, 0])
        .nice();

      // Create axes
      const xAxis = d3.axisBottom(xScale);
      const yAxis = d3.axisLeft(yScale);

      // Add x-axis
      g.append('g')
        .attr('class', 'x-axis')
        .attr('transform', `translate(0,${innerHeight})`)
        .call(xAxis)
        .append('text')
        .attr('fill', 'black')
        .attr('text-anchor', 'middle')
        .attr('x', innerWidth / 2)
        .attr('y', 40)
        .text(xColumn);

      // Add y-axis
      g.append('g')
        .attr('class', 'y-axis')
        .call(yAxis)
        .append('text')
        .attr('fill', 'black')
        .attr('text-anchor', 'middle')
        .attr('transform', 'rotate(-90)')
        .attr('x', -innerHeight / 2)
        .attr('y', -40)
        .text(yColumn);

      // Add dots
      const dots = g
        .selectAll('circle')
        .data(data)
        .join('circle')
        .attr('cx', d => xScale(+d[xColumn]))
        .attr('cy', d => yScale(+d[yColumn]))
        .attr('r', 5)
        .attr('fill', d3Config.colors ? d3Config.colors[0] : 'steelblue')
        .attr('opacity', 0.7);

      // Add tooltips
      dots.append('title').text(d => {
        let tooltip = '';
        for (const [key, value] of Object.entries(d)) {
          tooltip += `${key}: ${value}\n`;
        }
        return tooltip;
      });
    } else {
      // Fall back to showing a message
      g.append('text')
        .attr('x', innerWidth / 2)
        .attr('y', innerHeight / 2)
        .attr('text-anchor', 'middle')
        .text('Custom D3 visualization - insufficient data or columns for automatic visualization');
    }

    // Add zoom behavior if configured
    if (d3Config.interactive !== false) {
      svg.call(
        d3
          .zoom()
          .extent([
            [0, 0],
            [width, height]
          ])
          .scaleExtent([0.5, 8])
          .on('zoom', event => {
            g.attr('transform', event.transform);
          })
      );
    }
  },

  /**
   * Drag handler for interactive nodes
   *
   * @param {Object} simulation - The D3 force simulation
   * @returns {Function} - The drag behavior
   */
  drag: function (simulation) {
    function dragstarted(event) {
      if (!event.active) simulation.alphaTarget(0.3).restart();
      event.subject.fx = event.subject.x;
      event.subject.fy = event.subject.y;
    }

    function dragged(event) {
      event.subject.fx = event.x;
      event.subject.fy = event.y;
    }

    function dragended(event) {
      if (!event.active) simulation.alphaTarget(0);
      event.subject.fx = null;
      event.subject.fy = null;
    }

    return d3.drag().on('start', dragstarted).on('drag', dragged).on('end', dragended);
  }
};

if (typeof window !== 'undefined') {
  window.d3Renderer = d3Renderer;
}
